import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAccount } from '@/api/useAccount';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { formatVehicleDate } from '@/components/fleet/formatVehicleDate';
import { assignmentAppliesOn } from '@/lib/vehicleAssignments';
import { todayInZone } from '@/lib/timezone';
import {
  VehicleAssignmentsNotPermittedError,
  useUserVehicleAssignments,
} from '@/routes/fleet/useFleet';

/**
 * What this person is driving, and what they used to (M1-27) — the AC's "a
 * tech's current vehicle shows on their profile".
 *
 * <b>Keyed on the USER id</b>, like M1-38's skills and M1-39's availability and
 * unlike M1-37's licences. The visible consequence is the same one M1-38
 * records: this section renders on the "no profile yet" branch of
 * `TechnicianProfileDetail` too, which matters because the real dev account
 * holds three Technicians and ZERO technician profiles — on a profile key this
 * section would be dead for every person in it.
 *
 * <b>Read-only, with no affordance at all.</b> Assigning a vehicle is
 * `users.manage` and there is no self-edit seam, so a technician gets no
 * button here rather than one that renders and then 403s — the same call M1-37,
 * M1-38 and M1-39 each made. Editing happens on the vehicle's own page, which
 * is where the cross-row rules can be explained in terms of the truck.
 *
 * ⚠️ The current assignment carries a LINK to the vehicle, but only for a
 * viewer who can follow it: `/fleet/:id` is `users.read`-gated, and a
 * technician holds no user permission at all. Rendering the link for them
 * would be an invitation to a Forbidden page.
 */
export function TechnicianVehicleSection({
  userId,
  canViewFleet,
}: {
  userId: string;
  /**
   * Whether this viewer holds `users.read` — i.e. whether `/fleet` is reachable
   * for them at all. Not "can they manage": a Dispatcher can look the truck up
   * and cannot edit it, and both are true at once.
   */
  canViewFleet: boolean;
}) {
  const { t } = useTranslation();
  const assignments = useUserVehicleAssignments(userId);
  // The business's own clock, not the viewer's — the same one the dispatch
  // board picks its default date on. Until it has loaded there is no date to
  // judge against, so the status badge simply holds off rather than guessing.
  const account = useAccount();
  const today = account.data ? todayInZone(account.data.timezone) : null;

  // A technician viewing someone else's profile: the API refuses, and that is a
  // real state rather than a load failure to retry.
  if (assignments.error instanceof VehicleAssignmentsNotPermittedError) {
    return null;
  }

  return (
    <section className="space-y-2 rounded-lg border p-4" data-testid="technician-vehicles">
      <h2 className="text-lg font-semibold">{t('technicianVehicles.heading')}</h2>

      {assignments.isPending ? (
        <LoadingState label={t('technicianVehicles.loading')} />
      ) : assignments.isError ? (
        // ⚠️ An error says so. It does NOT fall through to "no vehicle
        // assigned" — that is a positive claim nobody made, and M1-39's review
        // found exactly this shape shipping on the board's tooltip, where a
        // technician with an expired licence read as having none.
        <ErrorState
          title={t('technicianVehicles.loadErrorTitle')}
          onRetry={() => void assignments.refetch()}
        />
      ) : assignments.data.length === 0 ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('technicianVehicles.empty')}
        </p>
      ) : (
        <ul className="space-y-2">
          {assignments.data.map((assignment) => {
            // ⚠️ TWO predicates, because these are two different questions.
            //
            // `openEnded` is about the ROW's shape and drives the PHRASING:
            // "Since Apr 1" for a stint with no end date, "Jan 1 - Mar 31" for
            // one that has both. Keying that on "does it cover today" would
            // hide the end date of a dated stint that is currently running.
            //
            // `appliesToday` is about the CALENDAR and drives the status
            // badge, which is a fact about the truck right now grafted onto a
            // history row. This used to be `effectiveTo === null` as well, so a
            // dated stint running today was treated as history and the badge
            // never appeared — while the board's tooltip, which asks the server
            // a real range question, warned about the same van on the same day.
            const openEnded = assignment.effectiveTo == null;
            const appliesToday = today !== null && assignmentAppliesOn(assignment, today);
            return (
              <li key={assignment.id} className="text-sm">
                <span className="font-medium">
                  {canViewFleet ? (
                    <Link
                      to={`/fleet/${assignment.vehicleId}`}
                      className="underline underline-offset-2"
                    >
                      {assignment.vehicleLabel}
                    </Link>
                  ) : (
                    assignment.vehicleLabel
                  )}
                </span>
                {assignment.vehiclePlate ? (
                  <span className="ml-2 text-xs text-[var(--color-muted-foreground)]">
                    {assignment.vehiclePlate}
                  </span>
                ) : null}
                {assignment.isPrimaryDriver ? (
                  <span className="ml-2 text-xs text-[var(--color-muted-foreground)]">
                    {t('technicianVehicles.primary')}
                  </span>
                ) : null}
                {/* The vehicle's own status rides on the assignment row, which
                    is what lets a technician — who can never list the fleet —
                    still be told their van is in the shop. Words, not a shade
                    (web/CONVENTIONS.md's palette rule). */}
                {assignment.vehicleStatus &&
                assignment.vehicleStatus !== 'Active' &&
                appliesToday ? (
                  <span className="ml-2 text-xs font-medium text-[var(--color-destructive)]">
                    {t(`fleet.status.${assignment.vehicleStatus}`)}
                  </span>
                ) : null}
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {openEnded
                    ? t('technicianVehicles.since', {
                        from: formatVehicleDate(assignment.effectiveFrom),
                      })
                    : t('technicianVehicles.between', {
                        from: formatVehicleDate(assignment.effectiveFrom),
                        to: formatVehicleDate(assignment.effectiveTo!),
                      })}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
